// WarehouseOrderItemForm.tsx
import React, { useEffect } from 'react';
import { Service } from '../../../../app/models/Service';
import { Product } from '../../../../app/models/Product';
import { Currency } from '../../../../app/models/Currency';
import { Unit } from '../../../../app/models/Unit';
import { Depot } from '../../../../app/models/Depot';
import { WarehouseOrderItemDataForm } from '../../../../app/models/WarehouseOrderItem';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import LazyLoading from '../../../../components/LazyLoading';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';

interface Props {
  warehouseOrderItemForm: WarehouseOrderItemDataForm;
  setWarehouseOrderItemForm: (form: WarehouseOrderItemDataForm) => void;
  services: Service[];
  products: Product[];
  currencies: Currency[];
  quantityUnits: Unit[];
  units: Unit[];
  depots: Depot[];
  disableUpdate?: boolean;
  disableStorage?: boolean;
  disableRate?: boolean;
  loadingStorageInfo?: boolean;
  errorFields?: any;
  isWarehouseForm?: boolean;
}

const WarehouseOrderItemForm: React.FC<Props> = ({
  warehouseOrderItemForm,
  setWarehouseOrderItemForm,
  quantityUnits,
  services,
  products,
  currencies,
  units,
  depots,
  disableUpdate = false,
  disableStorage = false,
  disableRate = false,
  loadingStorageInfo,
  errorFields,
  isWarehouseForm = false,
}) => {
  const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

  useEffect(() => {
    setErrors(errorFields);
  }, [errorFields]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setWarehouseOrderItemForm({ ...warehouseOrderItemForm, [name]: value });
  };

  const handleChangeSelectTwo = (field: string, value: number | string) => {
    setWarehouseOrderItemForm({ ...warehouseOrderItemForm, [field]: value });
  };

  if (!warehouseOrderItemForm) return null;

  return (
    <>
      {/* Servicio */}
      <div className="mb-2 row">
        <label className="col-md-3 col-form-label">Servicio</label>
        <div className="col-md-9">
          <SelectTwo
            name="service_id"
            id="service_id"
            inputValue={warehouseOrderItemForm.service_id}
            options={SelectTwoMapperOptions(services, 'id', ['name'])}
            onChange={(v) => handleChangeSelectTwo('service_id', v.value)}
            placeholder="Seleccione un servicio"
            disabled={disableUpdate}
            onFocus={() => onFocusRemove('service_id')}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>
            {fieldErrorMessage('service_id')}
          </div>
        </div>
      </div>

      {/* Producto */}
      <div className="mb-2 row">
        <label className="col-md-3 col-form-label">Producto</label>
        <div className="col-md-9">
          <SelectTwo
            name="product_id"
            id="product_id"
            inputValue={warehouseOrderItemForm.product_id}
            options={SelectTwoMapperOptions(products, 'id', ['name'])}
            onChange={(v) => handleChangeSelectTwo('product_id', v.value)}
            placeholder="Seleccione un producto"
            disabled={disableUpdate}
            onFocus={() => onFocusRemove('product_id')}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>
            {fieldErrorMessage('product_id')}
          </div>
        </div>
      </div>

      {/* Fecha Cuota */}
      <div className="mb-2 row">
        <label className="col-md-3 col-form-label">Fecha Cuota</label>
        <div className="col-md-9">
          <input
            type="date"
            className="form-control"
            name="quota_date"
            value={warehouseOrderItemForm.quota_date || ''}
            onChange={handleChange}
            disabled={disableUpdate}
            onFocus={() => onFocusRemove('quota_date')}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>
            {fieldErrorMessage('quota_date')}
          </div>
        </div>
      </div>

      {/* Cantidad y unidad */}
      <div className="mb-2 row">
        <label className="col-md-3 col-form-label">Cantidad</label>
        <div className="col-md-9">
          <div className="row g-1">
            <div className="col-6">
              <input
                type="number"
                name="quantity"
                className="form-control"
                value={warehouseOrderItemForm.quantity}
                onChange={handleChange}
                placeholder="Ingrese cantidad"
                disabled={disableUpdate}
                onFocus={() => onFocusRemove('quantity')}
              />
              <div className="invalid-feedback" style={{ display: 'flex' }}>
                {fieldErrorMessage('quantity')}
              </div>
            </div>
            <div className="col-6">
              <SelectTwo
                name="quantity_unit_id"
                id="quantity_unit_id"
                inputValue={warehouseOrderItemForm.quantity_unit_id}
                options={SelectTwoMapperOptions(quantityUnits)}
                onChange={(v) => handleChangeSelectTwo('quantity_unit_id', v.value)}
                placeholder="medida"
                disabled={disableUpdate}
                onFocus={() => onFocusRemove('quantity_unit_id')}
              />
              <div className="invalid-feedback" style={{ display: 'flex' }}>
                {fieldErrorMessage('quantity_unit_id')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Almacén — solo formulario WAREHOUSE */}
      {isWarehouseForm && (
        <div className="mb-2 row">
          <label className="col-md-3 col-form-label">Almacén</label>
          <div className="col-md-9">
            <SelectTwo
              name="depot_id"
              id="depot_id"
              inputValue={warehouseOrderItemForm.depot_id}
              options={SelectTwoMapperOptions(depots, 'id', ['name'])}
              onChange={(v) => handleChangeSelectTwo('depot_id', v.value)}
              placeholder="Seleccione un almacén"
              disabled={disableUpdate}
              onFocus={() => onFocusRemove('depot_id')}
            />
            <div className="invalid-feedback" style={{ display: 'flex' }}>
              {fieldErrorMessage('depot_id')}
            </div>
          </div>
        </div>
      )}

      {/* Precio Unitario + moneda + unidad */}
      <div className="mb-2 row">
        <label className="col-md-3 col-form-label">Precio Unitario Venta</label>
        <div className="col-md-9">
          <div className="row g-1">
            <div className="col-6">
              <input
                type="number"
                className="form-control"
                name="unit_price"
                value={warehouseOrderItemForm.unit_price ?? ''}
                onChange={handleChange}
                placeholder="Ej: 12000"
                disabled={disableUpdate}
                onFocus={() => onFocusRemove('unit_price')}
              />
              <div className="invalid-feedback" style={{ display: 'flex' }}>
                {fieldErrorMessage('unit_price')}
              </div>
            </div>
            <div className="col-3">
              <SelectTwo
                name="currency_id"
                id="currency_id"
                inputValue={warehouseOrderItemForm.currency_id}
                options={SelectTwoMapperOptions(currencies, 'id', ['name'])}
                onChange={(v) => handleChangeSelectTwo('currency_id', v.value)}
                placeholder="Moneda"
                disabled={disableUpdate}
                onFocus={() => onFocusRemove('currency_id')}
              />
              <div className="invalid-feedback" style={{ display: 'flex' }}>
                {fieldErrorMessage('currency_id')}
              </div>
            </div>
            <div className="col-3">
              <SelectTwo
                name="rate_unit_id"
                id="rate_unit_id"
                inputValue={warehouseOrderItemForm.rate_unit_id}
                options={SelectTwoMapperOptions(quantityUnits)}
                onChange={(v) => handleChangeSelectTwo('rate_unit_id', v.value)}
                placeholder="Unidad"
                disabled={disableUpdate}
                onFocus={() => onFocusRemove('rate_unit_id')}
              />
              <div className="invalid-feedback" style={{ display: 'flex' }}>
                {fieldErrorMessage('rate_unit_id')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Campos exclusivos del formulario WAREHOUSE */}
      {isWarehouseForm &&
        (loadingStorageInfo ? (
          <LazyLoading height={100} />
        ) : (
          <div className="mb-2 row">
            <label className="col-md-3 col-form-label">Días libres</label>
            <div className="col-md-9">
              <input
                type="number"
                className="form-control"
                name="free_days"
                value={warehouseOrderItemForm.free_days ?? ''}
                onChange={handleChange}
                placeholder="Cantidad de días sin cobro"
                disabled={disableStorage}
                onFocus={() => onFocusRemove('free_days')}
              />
              <div className="invalid-feedback" style={{ display: 'flex' }}>
                {fieldErrorMessage('free_days')}
              </div>
            </div>
          </div>
        ))}

      {/* Comentarios */}
      <div className="mb-2 row">
        <label className="col-md-3 col-form-label">Comentarios</label>
        <div className="col-md-9">
          <textarea
            className="form-control"
            name="comment"
            value={warehouseOrderItemForm.comment || ''}
            onChange={handleChange}
            placeholder="Observaciones opcionales"
            disabled={disableUpdate}
            onFocus={() => onFocusRemove('comment')}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>
            {fieldErrorMessage('comment')}
          </div>
        </div>
      </div>
    </>
  );
};

export default WarehouseOrderItemForm;
